import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { confirmDialog } from "primereact/confirmdialog";
import { formatDelegationTaskSteps } from "../utils/delegationTaskFormatting";

type Props = {
    visible: boolean;
    residentId: string;
    providerMode: boolean;
    selectionMode?: boolean;
    backendAPIStore: any;
    onHide: () => void;
    onContinue?: (tasks: any[]) => void;
};

const emptyTask = {
    taskName: "",
    delegatedTaskAndExpectedOutcome: "",
    specificMedicationsDelegated: "",
    stepsToPerformTask: "",
    whatToReportToRnd: "",
    whatToReportToHealthCareProvider: "",
    whatToReportTo911: "",
};

const DelegationTaskManagementDialog = ({ visible, residentId, providerMode, selectionMode = false, backendAPIStore, onHide, onContinue }: Props) => {
    const [tasks, setTasks] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [editingTask, setEditingTask] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [generatingAI, setGeneratingAI] = useState(false);
    const [taskSearch, setTaskSearch] = useState("");

    const { suggestedTaskRows, predefinedTaskRows } = useMemo(() => {
        const query = taskSearch.trim().toLowerCase();
        const sortTasks = (taskRows: any[]) => [...taskRows].sort((first: any, second: any) => {
            const rank = (task: any) => selectedIds.includes(String(task?.taskId || "")) ? 0 : 1;
            return rank(first) - rank(second)
                || String(first?.taskName || "").localeCompare(String(second?.taskName || ""), undefined, { sensitivity: "base" });
        });
        const visibleTasks = query
            ? tasks.filter((task: any) => [task?.taskName, task?.category, task?.delegatedTaskAndExpectedOutcome]
                .some((value) => String(value || "").toLowerCase().includes(query)))
            : tasks;
        return {
            suggestedTaskRows: sortTasks(visibleTasks.filter((task: any) => task?.source === "ai-suggestion")),
            predefinedTaskRows: sortTasks(visibleTasks.filter((task: any) => task?.source !== "ai-suggestion")),
        };
    }, [taskSearch, tasks, selectedIds]);

    const loadTasks = async () => {
        if (!residentId) return;
        setLoading(true);
        try {
            await backendAPIStore.init();
            const response = providerMode
                ? await backendAPIStore.getProviderDelegationTasks(residentId)
                : await backendAPIStore.getNurseDelegationTasks(residentId);
            const nextTasks = Array.isArray(response?.tasks) ? response.tasks : [];
            setTasks(nextTasks);
            setTaskSearch("");
            setSelectedIds([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) void loadTasks();
    }, [visible, residentId, providerMode, selectionMode]);

    const saveTask = async () => {
        if (!editingTask?.taskName?.trim()) return;
        const save = providerMode ? backendAPIStore.saveProviderDelegationTask : backendAPIStore.saveNurseDelegationTask;
        await save.call(backendAPIStore, {
            residentId,
            taskId: editingTask.taskId,
            taskName: editingTask.taskName.trim(),
            delegatedTaskAndExpectedOutcome: editingTask.delegatedTaskAndExpectedOutcome,
            specificMedicationsDelegated: editingTask.specificMedicationsDelegated,
            stepsToPerformTask: formatDelegationTaskSteps(editingTask.stepsToPerformTask),
            whatToReportToRnd: editingTask.whatToReportToRnd,
            whatToReportToHealthCareProvider: editingTask.whatToReportToHealthCareProvider,
            whatToReportTo911: editingTask.whatToReportTo911,
        });
        setEditingTask(null);
        await loadTasks();
    };

    const requestDeleteTask = (task: any) => {
        if (selectionMode || !task?.taskId) return;
        confirmDialog({
            message: `Delete “${task.taskName}” from this resident's nursing tasks?`,
            header: "Delete Nursing Task",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                setLoading(true);
                try {
                    const remove = providerMode ? backendAPIStore.deleteProviderDelegationTask : backendAPIStore.deleteNurseDelegationTask;
                    await remove.call(backendAPIStore, residentId, String(task.taskId));
                    setSelectedIds((current) => current.filter((id) => id !== String(task.taskId)));
                    await loadTasks();
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const generateTaskFromAI = async () => {
        if (!editingTask?.taskName?.trim() || generatingAI) return;
        setGeneratingAI(true);
        try {
            const response = await backendAPIStore.formatAiText({
                text: [
                    `Task name: ${editingTask.taskName.trim()}`,
                    `Expected outcome: ${editingTask.delegatedTaskAndExpectedOutcome || "Not provided"}`,
                    `Medication details: ${editingTask.specificMedicationsDelegated || "Not provided"}`,
                    `Current steps: ${editingTask.stepsToPerformTask || "Not provided"}`,
                ].join("\n"),
                taskType: "professional-note-cleanup",
                tone: "professional",
                domain: "delegation-task",
                outputStyle: "delegation_task_json",
                metadata: {
                    taskName: editingTask.taskName.trim(),
                    currentExpectedOutcome: editingTask.delegatedTaskAndExpectedOutcome || "",
                    currentMedicationDetails: editingTask.specificMedicationsDelegated || "",
                    currentSteps: editingTask.stepsToPerformTask || "",
                },
            });
            const generatedText = String(response?.formattedText || "").trim();
            const generatedJson = generatedText
                .replace(/^```(?:json)?\s*/i, "")
                .replace(/\s*```$/i, "")
                .trim();
            const generated = JSON.parse(generatedJson);
            setEditingTask((current: any) => ({
                ...current,
                delegatedTaskAndExpectedOutcome: String(generated?.delegatedTaskAndExpectedOutcome || current?.delegatedTaskAndExpectedOutcome || "").trim(),
                specificMedicationsDelegated: String(generated?.specificMedicationsDelegated || current?.specificMedicationsDelegated || "").trim(),
                stepsToPerformTask: String(generated?.stepsToPerformTask || current?.stepsToPerformTask || "").trim(),
                whatToReportToRnd: String(generated?.whatToReportToRnd || current?.whatToReportToRnd || "").trim(),
                whatToReportToHealthCareProvider: String(generated?.whatToReportToHealthCareProvider || current?.whatToReportToHealthCareProvider || "").trim(),
                whatToReportTo911: String(generated?.whatToReportTo911 || current?.whatToReportTo911 || "").trim(),
            }));
        } finally {
            setGeneratingAI(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            {!selectionMode ? <Button type="button" label="Add New Task" icon="pi pi-plus" severity="warning" onClick={() => setEditingTask({ ...emptyTask })} /> : null}
            <Button type="button" label="Cancel" severity="secondary" outlined onClick={onHide} />
            {selectionMode ? <Button type="button" label="Continue" icon="pi pi-arrow-right" severity="warning" onClick={() => onContinue?.(tasks.filter((task) => selectedIds.includes(String(task.taskId))))} disabled={!selectedIds.length || loading} /> : null}
        </div>
    );

    const taskEditorFooter = editingTask ? (
        <div
            className="flex justify-content-end gap-2"
            style={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                background: "#ffffff",
                borderTop: "1px solid #e2e8f0",
                padding: "0.75rem 0 0",
            }}
        >
            <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => setEditingTask(null)} />
            <Button type="button" label="Save Task" icon="pi pi-check" severity="warning" onClick={() => void saveTask()} disabled={!editingTask.taskName?.trim()} />
        </div>
    ) : null;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            style={{ width: "min(58rem, 96vw)" }}
            header={selectionMode ? "Select Nursing Tasks for Form 13-678" : "Manage Nursing Tasks for Form 13-678"}
            footer={footer}
        >
            <div className="flex flex-column gap-3">
                <div className="flex justify-content-between align-items-center gap-2">
                    {selectionMode ? <Button type="button" label={selectedIds.length === tasks.filter((task) => task.active !== false).length ? "Clear all" : "Select all"} severity="warning" outlined size="small" onClick={() => setSelectedIds(selectedIds.length === tasks.filter((task) => task.active !== false).length ? [] : tasks.filter((task) => task.active !== false).map((task) => String(task.taskId)))} /> : null}
                </div>
                <span className="p-input-icon-left" style={{ width: "min(26rem, 100%)", display: "block", margin: "0 auto" }}>
                    <i className="pi pi-search" style={{ left: "0.85rem" }} />
                    <InputText
                        value={taskSearch}
                        onChange={(event) => setTaskSearch(event.target.value)}
            placeholder="Search tasks"
            aria-label="Search nursing tasks"
                        style={{ width: "100%", paddingLeft: "2.5rem" }}
                    />
                </span>
                {!tasks.length && !loading ? <div style={{ color: "#64748b" }}>No delegation tasks found.</div> : null}
                {tasks.length > 0 && !suggestedTaskRows.length && !predefinedTaskRows.length ? <div style={{ color: "#64748b" }}>No delegation tasks match your search.</div> : null}
                {suggestedTaskRows.length ? <div className="flex flex-column gap-2" style={{ marginTop: "0.5rem" }}>
                    <div className="flex align-items-center justify-content-between gap-2" style={{ background: "linear-gradient(90deg, #fff7ed 0%, #fffbeb 100%)", border: "1px solid #fed7aa", borderRadius: "0.75rem", padding: "0.75rem 1rem" }}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-sparkles" style={{ color: "#c2410c", fontSize: "1.1rem" }} />
                            <div>
                                <div style={{ color: "#9a3412", fontWeight: 800 }}>Suggested by Hub based on resident document</div>
                                <div style={{ color: "#9a3412", fontSize: "0.8rem", marginTop: "0.15rem" }}>Review these nursing delegation tasks before starting the job.</div>
                            </div>
                        </div>
                        <Tag value={String(suggestedTaskRows.length)} severity="warning" rounded />
                    </div>
                    {!suggestedTaskRows.length ? <div style={{ color: "#64748b", padding: "0.5rem 0.25rem" }}>No suggested nursing tasks found.</div> : suggestedTaskRows.map((task) => {
                        const selected = selectedIds.includes(String(task.taskId));
                        return (
                            <div key={task.taskId} className="flex align-items-start gap-3" style={{ borderBottom: "1px solid #e2e8f0", padding: "0.75rem 0" }}>
                                {selectionMode ? <Checkbox inputId={`delegation-task-${task.taskId}`} checked={selected} disabled={task.active === false} onChange={() => setSelectedIds((current) => selected ? current.filter((id) => id !== String(task.taskId)) : [...current, String(task.taskId)])} /> : null}
                                <div className="flex-1" style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700 }}>{task.taskName}</div>
                                    <div style={{ color: "#64748b", fontSize: "0.85rem" }}>{task.delegatedTaskAndExpectedOutcome || "No expected outcome entered."}</div>
                                </div>
                                <div className="flex align-items-center gap-1">
                                    <Button type="button" icon="pi pi-pencil" severity="secondary" text rounded onClick={() => setEditingTask({ ...task, stepsToPerformTask: formatDelegationTaskSteps(task.stepsToPerformTask), taskId: task.source === "catalog" ? "" : task.taskId, source: "resident" })} />
                                    {!selectionMode ? <Button type="button" icon="pi pi-trash" severity="danger" outlined rounded tooltip="Delete task" tooltipOptions={{ position: "top" }} onClick={() => requestDeleteTask(task)} aria-label={`Delete ${task.taskName}`} style={{ color: "#dc2626", borderColor: "#fca5a5" }} /> : null}
                                </div>
                            </div>
                        );
                    })}
                </div> : null}
                <div className="flex flex-column gap-2" style={{ marginTop: "0.5rem" }}>
                    <div className="flex align-items-center justify-content-between gap-2" style={{ background: "linear-gradient(90deg, #eff6ff 0%, #f8fafc 100%)", border: "1px solid #bfdbfe", borderRadius: "0.75rem", padding: "0.75rem 1rem" }}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-list" style={{ color: "#1d4ed8", fontSize: "1.1rem" }} />
                            <div>
                                <div style={{ color: "#1e3a8a", fontWeight: 800 }}>Predefined tasks</div>
                                <div style={{ color: "#1e40af", fontSize: "0.8rem", marginTop: "0.15rem" }}>Existing nursing delegation tasks available for this resident.</div>
                            </div>
                        </div>
                        <Tag value={String(predefinedTaskRows.length)} severity="info" rounded />
                    </div>
                    {predefinedTaskRows.map((task) => {
                        const selected = selectedIds.includes(String(task.taskId));
                        return (
                            <div key={task.taskId} className="flex align-items-start gap-3" style={{ borderBottom: "1px solid #e2e8f0", padding: "0.75rem 0" }}>
                                {selectionMode ? <Checkbox inputId={`delegation-task-${task.taskId}`} checked={selected} disabled={task.active === false} onChange={() => setSelectedIds((current) => selected ? current.filter((id) => id !== String(task.taskId)) : [...current, String(task.taskId)])} /> : null}
                                <div className="flex-1" style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700 }}>{task.taskName}</div>
                                    <div style={{ color: "#64748b", fontSize: "0.85rem" }}>{task.delegatedTaskAndExpectedOutcome || "No expected outcome entered."}</div>
                                </div>
                                <div className="flex align-items-center gap-1">
                                    <Button type="button" icon="pi pi-pencil" severity="secondary" text rounded onClick={() => setEditingTask({ ...task, stepsToPerformTask: formatDelegationTaskSteps(task.stepsToPerformTask), taskId: task.source === "catalog" ? "" : task.taskId, source: "resident" })} />
                                    {!selectionMode ? <Button type="button" icon="pi pi-trash" severity="danger" outlined rounded tooltip="Delete task" tooltipOptions={{ position: "top" }} onClick={() => requestDeleteTask(task)} aria-label={`Delete ${task.taskName}`} style={{ color: "#dc2626", borderColor: "#fca5a5" }} /> : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <Dialog visible={!!editingTask} onHide={() => setEditingTask(null)} modal header="Nursing Task Details" style={{ width: "min(48rem, 94vw)" }} footer={taskEditorFooter}>
                {editingTask ? <div className="grid">
                    <div className="col-12"><label className="block mb-2">Task Name</label><InputText value={editingTask.taskName} onChange={(e) => setEditingTask({ ...editingTask, taskName: e.target.value })} style={{ width: "100%" }} /><div className="flex justify-content-center mt-2"><Button type="button" label={generatingAI ? "Auto Generating ..." : "Auto Generate"} icon="pi pi-sparkles" severity="warning" outlined onClick={() => void generateTaskFromAI()} disabled={!editingTask.taskName?.trim() || generatingAI} /></div></div>
                    <div className="col-12"><label className="block mb-2">Delegated Task and Expected Outcome</label><InputTextarea value={editingTask.delegatedTaskAndExpectedOutcome} onChange={(e) => setEditingTask({ ...editingTask, delegatedTaskAndExpectedOutcome: e.target.value })} rows={3} style={{ width: "100%" }} /></div>
                    <div className="col-12"><label className="block mb-2">Specific Medications Delegated</label><InputTextarea value={editingTask.specificMedicationsDelegated} onChange={(e) => setEditingTask({ ...editingTask, specificMedicationsDelegated: e.target.value })} rows={4} style={{ width: "100%" }} /></div>
                    <div className="col-12"><label className="block mb-2">Steps to Perform Task</label><InputTextarea value={editingTask.stepsToPerformTask} onChange={(e) => setEditingTask({ ...editingTask, stepsToPerformTask: e.target.value })} rows={6} style={{ width: "100%", whiteSpace: "pre-wrap" }} /></div>
                    <div className="col-12"><label className="block mb-2">What to Report to RND</label><InputTextarea value={editingTask.whatToReportToRnd || ""} onChange={(e) => setEditingTask({ ...editingTask, whatToReportToRnd: e.target.value })} rows={3} style={{ width: "100%" }} /></div>
                    <div className="col-12"><label className="block mb-2">What to Report to Health Care Provider</label><InputTextarea value={editingTask.whatToReportToHealthCareProvider || ""} onChange={(e) => setEditingTask({ ...editingTask, whatToReportToHealthCareProvider: e.target.value })} rows={3} style={{ width: "100%" }} /></div>
                    <div className="col-12"><label className="block mb-2">What to Report to 911</label><InputTextarea value={editingTask.whatToReportTo911 || ""} onChange={(e) => setEditingTask({ ...editingTask, whatToReportTo911: e.target.value })} rows={3} style={{ width: "100%" }} /></div>
                </div> : null}
            </Dialog>
        </Dialog>
    );
};

export default DelegationTaskManagementDialog;
